import React from "react";
import { Card } from "@/components/ui/card";
import { formatLKR, formatPct } from "@/lib/api";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

const ShareClassComparisonCard = ({ comparison }) => {
  if (!comparison) return null;
  const {
    voting_price,
    non_voting_price,
    price_discount_pct,
    voting_yield,
    non_voting_yield,
    yield_difference_pct,
    dividend_per_share,
  } = comparison;

  return (
    <Card data-testid="share-class-comparison-card" className="p-5 space-y-4 border bg-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold">Voting vs Non-voting</div>
          <div className="text-xs text-muted-foreground">Same DPS · different price · different yield</div>
        </div>
      </div>

      <p className="text-sm leading-relaxed" data-testid="share-comparison-sentence">
        Non-voting trades at a{" "}
        <span className="font-semibold text-[hsl(var(--info))] tabular">{Math.abs(price_discount_pct).toFixed(2)}%</span>{" "}
        discount — yield advantage of{" "}
        <span className="font-semibold text-[hsl(var(--success))] tabular">{Math.abs(yield_difference_pct).toFixed(2)}%</span>.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border bg-background p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Voting price</div>
          <div className="mt-1 text-lg font-semibold tabular">{formatLKR(voting_price)}</div>
          <div className="mt-2 text-xs text-muted-foreground">Yield</div>
          <div className="text-sm font-medium tabular">{formatPct(voting_yield)}</div>
        </div>
        <div className="rounded-lg border bg-background p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Non-voting price</div>
          <div className="mt-1 text-lg font-semibold tabular">{formatLKR(non_voting_price)}</div>
          <div className="mt-2 text-xs text-muted-foreground">Yield</div>
          <div className="text-sm font-medium tabular text-[hsl(var(--success))]">{formatPct(non_voting_yield)}</div>
        </div>
      </div>

      <div
        data-testid="share-class-yield-gap"
        className="rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/12 p-3"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wide text-[hsl(var(--warning-foreground))]/70">
            Yield uplift on non-voting
          </div>
          <div className="flex items-center gap-1 text-[hsl(var(--success))]">
            <ArrowUpRight className="h-4 w-4" />
            <span className="text-base font-semibold tabular">{formatPct(yield_difference_pct)}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-1">
          <div className="text-xs uppercase tracking-wide text-[hsl(var(--warning-foreground))]/70">
            Price discount
          </div>
          <div className="flex items-center gap-1 text-[hsl(var(--info))]">
            <ArrowDownRight className="h-4 w-4" />
            <span className="text-base font-semibold tabular">{formatPct(-Math.abs(price_discount_pct))}</span>
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        Both share classes pay <span className="font-medium text-foreground">{formatLKR(dividend_per_share)}</span> per
        share — the lower non-voting price drives the higher yield.
      </div>
    </Card>
  );
};

export default ShareClassComparisonCard;
